import { useRef, useState } from 'react';

import { DeleteIcon } from '@chakra-ui/icons';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Image,
  VStack,
  HStack,
  Box,
  IconButton,
  ModalCloseButton,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { Card } from 'components/uikit';
import { useIsMobile } from 'utils/hooks/screenBreakpoints';

type PreviewFile = {
  file: File;
  previewUrl?: string;
};

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadClick: () => void;
  onUpload: (formData: FormData) => Promise<void>;
  isLoading: boolean;
}

export function PaymentUploadModal({
  isOpen,
  onClose,
  onUploadClick,
  onUpload,
  isLoading,
}: UploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<PreviewFile[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    const newFiles = selectedFiles.filter(
      (newFile) =>
        !files.some(
          ({ file }) =>
            file.name === newFile.name &&
            file.size === newFile.size &&
            file.lastModified === newFile.lastModified,
        ),
    );
    const newPreviewFiles = newFiles.map((file) => ({
      file,
      previewUrl:
        file.type.startsWith('image/') || file.type.startsWith('video/')
          ? URL.createObjectURL(file)
          : undefined,
    }));

    setFiles([...files, ...newPreviewFiles]);
  };

  const handleRemoveFile = (idx: number) => {
    const newFiles = files.filter((_, i) => i !== idx);
    setFiles(newFiles);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    const formData = new FormData();
    files.forEach(({ file }) => {
      formData.append('file', file, file.name);
    });
    await onUpload(formData);
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };
  const isMobile = useIsMobile();
  const { t } = useTranslation('payments');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={isMobile ? 'full' : 'lg'}>
      <ModalOverlay bg='rgba(0, 0, 0, 0.85)' backdropFilter='blur(4px)' />
      <ModalContent
        bg='grey.background'
        color='grey.primaryText'
        border='1px solid'
        borderColor='grey.border'
        maxH={['100vh', '80vh']}
      >
        <ModalHeader
          padding='16px'
          borderBottom='1px solid'
          borderColor='grey.border'
        >
          {t('payment_upload_modal_header')}
        </ModalHeader>
        <ModalCloseButton top='16px' right='16px' color='grey.primaryText' />
        <ModalBody
          display='flex'
          flexDirection='column'
          padding='16px 16px'
          justifyContent='start'
          gap='8px'
          maxH='100%'
          overflow='hidden'
        >
          <Text fontSize='md' fontWeight='400' color='grey.secondaryText'>
            {t('payment_upload_modal_description')}
          </Text>
          <input
            ref={fileInputRef}
            type='file'
            accept='image/*,application/pdf,video/*'
            multiple
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {files.length > 0 && (
            <VStack
              align='start'
              spacing='8px'
              w='100%'
              overflow='auto'
              maxH='100%'
            >
              {files.map(({ file, previewUrl }, idx) => (
                <Card
                  py='8px'
                  px='12px'
                  key={file.name + idx}
                  gap='8px'
                  w='100%'
                  alignItems='center'
                  boxShadow='none'
                >
                  <HStack
                    alignItems='start'
                    justifyContent='space-between'
                    w='100%'
                    spacing='0'
                  >
                    <Text
                      fontSize='sm'
                      noOfLines={1}
                      maxW='100%'
                      fontWeight='semibold'
                      color='grey.primaryText'
                    >
                      {file.name}
                    </Text>
                    <IconButton
                      aria-label={t('payment_upload_modal_remove_button')}
                      icon={<DeleteIcon color='error' />}
                      variant='ghost'
                      boxSize='16px'
                      w='16px'
                      minW='0'
                      onClick={() => handleRemoveFile(idx)}
                    />
                  </HStack>
                  {file.type.startsWith('image/') && previewUrl ? (
                    <Image
                      src={previewUrl}
                      maxH='200px'
                      objectFit='cover'
                      alt={file.name}
                      borderRadius='md'
                      border='1px solid'
                      borderColor='grey.border'
                    />
                  ) : file.type.startsWith('video/') && previewUrl ? (
                    <Box
                      maxH='200px'
                      borderRadius='md'
                      border='1px solid'
                      borderColor='grey.border'
                      overflow='hidden'
                      bg='blackAlpha.200'
                    >
                      <video src={previewUrl} style={{ objectFit: 'cover' }} />
                    </Box>
                  ) : file.type === 'application/pdf' ? (
                    <Box
                      maxH='200px'
                      bg='whiteAlpha.100'
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      borderRadius='md'
                      border='1px solid'
                      borderColor='grey.border'
                    >
                      <Text fontSize='xs' color='grey.primaryText'>
                        PDF
                      </Text>
                    </Box>
                  ) : (
                    <Box
                      boxSize='200px'
                      bg='whiteAlpha.100'
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      borderRadius='md'
                      border='1px solid'
                      borderColor='grey.border'
                    >
                      <Text fontSize='xs' color='grey.primaryText'>
                        {file.name.split('.').pop()?.toUpperCase()}
                      </Text>
                    </Box>
                  )}
                </Card>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter
          borderTop='1px solid'
          borderColor='grey.border'
          justifyContent='space-between'
          gap='24px'
          bg='transparent'
        >
          <Button
            w='50%'
            variant='paymentOutline'
            onClick={() => {
              onUploadClick();
              fileInputRef.current?.click();
            }}
            isLoading={isLoading}
          >
            {t('payment_upload_modal_choose_button')}
          </Button>
          <Button
            variant='success'
            w='50%'
            onClick={handleUpload}
            isLoading={isLoading}
            isDisabled={files.length === 0}
          >
            {t('payment_upload_modal_send_button')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
